import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { queryClient } from "@/lib/query-client";
import { buildAuthEmailRedirect } from "@/lib/app-origin";

/**
 * The backend client is imported dynamically so @supabase/supabase-js is not
 * part of the entry chunk downloaded before first paint on public marketing
 * pages. Auth behaviour is unchanged: the listener is still attached and the
 * initial session still resolved on mount, and `loading` stays true until it
 * does — exactly as before.
 */
const getSupabase = () =>
  import("@/integrations/supabase/client").then((m) => m.supabase);

interface AuthContextType {
  session: Session | null;
  user: User | null;
  loading: boolean;
  signUp: (email: string, password: string, fullName: string, emailRedirectTo?: string, extraMetadata?: Record<string, unknown>) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signInWithMagicLink: (email: string, emailRedirectTo?: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    let cancelled = false;

    void getSupabase().then((supabase) => {
      if (cancelled) return;

      // Set up auth state listener FIRST (before getSession) to avoid race conditions
      const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
        // Deferred cache clear: avoid wiping cache on transient SIGNED_OUT events
        // fired during auth bridge / redirect reconciliation.
        if (event === "SIGNED_OUT") {
          window.setTimeout(async () => {
            const { data: { session: currentSession } } = await supabase.auth.getSession();
            if (!currentSession) {
              queryClient.clear();
            }
          }, 0);
        }

        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      });
      unsubscribe = () => subscription.unsubscribe();

      // Then get initial session
      supabase.auth.getSession().then(({ data: { session }, error }) => {
        if (cancelled) return;
        if (error) {
          console.error("[AuthContext] getSession error:", error.message);
        }
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      });
    });

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, []);

  const signUp = async (email: string, password: string, fullName: string, emailRedirectTo?: string, extraMetadata?: Record<string, unknown>) => {
    const supabase = await getSupabase();
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: fullName, ...(extraMetadata ?? {}) },
        emailRedirectTo,
      },
    });
    return { error: error as Error | null };
  };

  const signIn = async (email: string, password: string) => {
    const supabase = await getSupabase();
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error as Error | null };
  };

  const signInWithMagicLink = async (email: string, emailRedirectTo?: string) => {
    const supabase = await getSupabase();
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: emailRedirectTo ?? buildAuthEmailRedirect("/auth/callback"),
      },
    });
    if (error) {
      console.error("[AuthContext] Magic link error:", error.message);
    }
    return { error: error as Error | null };
  };

  const signOut = async () => {
    const supabase = await getSupabase();
    await supabase.auth.signOut();
  };

  const resetPassword = async (email: string) => {
    const supabase = await getSupabase();
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: buildAuthEmailRedirect("/auth/reset-password"),
    });
    return { error: error as Error | null };
  };

  return (
    <AuthContext.Provider value={{ session, user, loading, signUp, signIn, signInWithMagicLink, signOut, resetPassword }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}

/** Signed-in user id, or null outside an AuthProvider / when signed out. */
export function useAuthUserId(): string | null {
  return useContext(AuthContext)?.user?.id ?? null;
}
